import { Col, PageHeader, Row, Table, Typography, Select, message } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "shared/components";
import { UserTeamType } from "shared/enums/UserTeamType";
import { Member } from "shared/interfaces/Member";
import { Team } from "shared/interfaces/Team";
import { deleteTeamMember, updateTeamInfo } from "shared/service/StrmService";
import AddMemberForm from "./AddMemberForm";
import "./TeamManagement.scss";
import { useSelector } from "react-redux";

interface TeamManagementProps {
  team?: Team;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  getTeams: () => void;
}

const TeamManagement: React.FC<TeamManagementProps> = (props) => {
  const { team, loading, setLoading, getTeams } = props;
  const { t } = useTranslation("radar");
  const [selectedMember, setSelectedMember] = useState<Member>();
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [inviteSucessVisibility, setInviteSucessVisibility] =
    useState<boolean>(false);
  const [deleteMemberSuccess, setDeleteMemberSuccess] =
    useState<boolean>(false);
  const [limitReachedModalVisible, setLimitReachedModalVisible] =
    useState<boolean>(false);
  const { loggedUser } = useSelector((state: any) => state.users);

  const onOkDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteTeamMember(selectedMember);
      if (response) setDeleteMemberSuccess(true);
      else message.error(t("pages.TeamManagement.deleteError"));
    } catch {
      message.error(t("pages.TeamManagement.deleteError"));
    }
    getTeams();
    setDeleteModalVisible(false);
    setLoading(false);
  };

  const onAddMember = async () => {
    getTeams();
    setShowAddModal(false);
    setInviteSucessVisibility(true);
  };

  const onCancelDelete = () => {
    setSelectedMember(undefined);
    setDeleteModalVisible(false);
  };

  const onChangeUserType = async (value: UserTeamType, member: Member) => {
    member.userTeamType = value;
    await updateTeamInfo({ ...team, userTeam: [member] });
    getTeams();
  };

  const columns: ColumnsType<Member> = [
    {
      title: t("pages.TeamManagement.name"),
      dataIndex: ["user", "name"],
      align: "center",
      render: (value: string) => <b>{value}</b>,
    },
    {
      title: t("pages.TeamManagement.email"),
      dataIndex: ["user", "login"],
      align: "center",
    },
    {
      title: t("pages.TeamManagement.status"),
      dataIndex: ["user", "cognitoUsername"],
      align: "center",
      render: (value: any, record: any) => (
        <>
          {value === "" ? (
            <span className="permissao-cor-vermelho">
              <b className="round"></b> {t("pages.TeamManagement.pendente")}
            </span>
          ) : (
            <span className="permissao-cor-verde">
              <b className="round"></b> {t("pages.TeamManagement.ativo")}
            </span>
          )}
        </>
      ),
    },
    {
      title: t("pages.TeamManagement.permission"),
      dataIndex: "userTeamType",
      render: (value: UserTeamType, record) => (
        <Select
          value={value}
          onChange={(value: UserTeamType) => onChangeUserType(value, record)}
          disabled={record?.user?.login === loggedUser?.login}
        >
          <Select.Option value={UserTeamType.Admin}>
            {t("pages.TeamManagement.admin")}
          </Select.Option>
          <Select.Option value={UserTeamType.Manager}>
            {t("pages.TeamManagement.manager")}
          </Select.Option>
          <Select.Option value={UserTeamType.Viewer}>
            {t("pages.TeamManagement.viewer")}
          </Select.Option>
        </Select>
      ),
      align: "center",
    },
    {
      align: "right",
      render: (text: any, record: any) => (
        <>
          {record?.user?.login !== loggedUser?.login && (
            <button
              className="botao-vermelho-vazado"
              onClick={() => {
                setSelectedMember(record);
                setDeleteModalVisible(true);
              }}
            >
              {t("pages.TeamManagement.remove")}
            </button>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Row>
        <Col xs={24}>
          <PageHeader
            title={team?.displayName}
            extra={[
              <button
                className="botao-amarelo-vazado"
                key="1"
                onClick={() =>
                  (team?.licenseAmount || 0) <= (team?.userTeam?.length || 0)
                    ? setLimitReachedModalVisible(true)
                    : setShowAddModal(true)
                }
              >
                {t("pages.TeamManagement.addUser")}
              </button>,
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <Table
            columns={columns}
            dataSource={team?.userTeam}
            pagination={false}
            loading={loading}
          />
        </Col>
      </Row>
      <Modal
        onOk={onOkDelete}
        onCancel={onCancelDelete}
        visible={deleteModalVisible}
        cancelText={t("pages.TeamManagement.no")}
        okText={t("pages.TeamManagement.yes")}
        okClass="botao-verde"
        cancelClass="botao-vermelho"
        width={"50%"}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Typography.Text
            style={{
              fontSize: 16,
              margin: 0,
              marginTop: 32,
              color: "#F97066",
              textAlign: "start",
              width: "100%",
            }}
          >
            {t("pages.TeamManagement.alert")}
          </Typography.Text>
          <Typography.Title
            level={4}
            style={{ color: "#fff", marginTop: 0 }}
            className="font-darwin-bold"
          >
            {t("pages.TeamManagement.deleteMessage")}
          </Typography.Title>
        </div>
      </Modal>
      <AddMemberForm
        visible={showAddModal}
        onSave={onAddMember}
        onCancel={() => setShowAddModal(false)}
        teamId={team?.id}
      />
      <Modal
        onOk={() => setLimitReachedModalVisible(false)}
        onCancel={() => setLimitReachedModalVisible(false)}
        visible={limitReachedModalVisible}
        showCancelButton={false}
        width={"50%"}
        okText="ok"
      >
        <div
          style={{
            minHeight: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography.Title
            level={4}
            style={{ color: "#E4BC5C" }}
            className="font-darwin-bold"
          >
            {t("pages.TeamManagement.limitReached")}
          </Typography.Title>
        </div>
      </Modal>
      <Modal
        onOk={() => setDeleteMemberSuccess(false)}
        visible={deleteMemberSuccess}
        showCancelButton={false}
        width={"50%"}
        okText="ok"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Typography.Text
            style={{
              fontSize: 16,
              margin: 0,
              marginTop: 32,
              color: "#32D583",
              textAlign: "start",
              width: "100%",
            }}
          >
            {t("pages.TeamManagement.completed")}
          </Typography.Text>
          <Typography.Text
            style={{ fontSize: 20, marginTop: 0, color: "#fff" }}
          >
            {t("pages.TeamManagement.deleteSuccess")}
          </Typography.Text>
        </div>
      </Modal>
      <Modal
        onOk={() => setInviteSucessVisibility(false)}
        onCancel={() => setInviteSucessVisibility(false)}
        visible={inviteSucessVisibility}
        showCancelButton={false}
        width={"50%"}
        okText="ok"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Typography.Text
            style={{
              fontSize: 16,
              margin: 0,
              marginTop: 32,
              color: "#32D583",
              textAlign: "start",
              width: "100%",
            }}
          >
            {t("pages.TeamManagement.completed")}
          </Typography.Text>
          <Typography.Text
            style={{ fontSize: 20, marginTop: 0, color: "#fff" }}
          >
            {t("pages.TeamManagement.inviteSuccess")}
          </Typography.Text>
        </div>
      </Modal>
    </>
  );
};

export default TeamManagement;
